import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { PhotoUpload, EvidenceThumb } from "../components/Evidence";
import { PrioritySelect } from "../components/Priority";
import { toast } from "sonner";

// Slice 2 (A): explicit fault-report USE decision. Ranked by restrictiveness; the configured
// minimum comes from the chosen severity and can only be ESCALATED, never downgraded.
const RANK = { safe_to_continue: 0, restricted_use: 1, stop_using: 2 };
const DEC = [
  { key: "safe_to_continue", label: "Safe to Continue", desc: "Yellow — keep using; active fault, repair required.", cls: "border-yellow-400 bg-yellow-50 text-yellow-900" },
  { key: "restricted_use", label: "Restricted Use", desc: "Amber — limited/conditional use with a documented basis.", cls: "border-amber-400 bg-amber-50 text-amber-900" },
  { key: "stop_using", label: "Stop Using", desc: "Red — out of service until repaired.", cls: "border-red-400 bg-red-50 text-red-900" },
];
const configuredMin = (sev) => {
  if (!sev) return "safe_to_continue";
  if (sev.blocks_use || sev.on_report_action === "out_of_service") return "stop_using";
  if (sev.on_report_action === "restrict") return sev.allow_restricted_use === false ? "stop_using" : "restricted_use";
  return "safe_to_continue";
};

export default function FaultReportPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const assetId = params.get("asset");
  const [asset, setAsset] = useState(null);
  const [cats, setCats] = useState([]);
  const [sevs, setSevs] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", category_id: "", severity_id: "", priority: "medium", location: "" });
  const [useDecision, setUseDecision] = useState("safe_to_continue");
  const [restrictionReason, setRestrictionReason] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const sev = sevs.find((s) => s.id === form.severity_id);
  const minDecision = configuredMin(sev);
  const allowed = DEC.filter((d) => RANK[d.key] >= RANK[minDecision]);

  useEffect(() => {
    if (assetId) api.get(`/assets/${assetId}`).then((r) => setAsset(r.data)).catch(() => {});
    api.get("/config/fault-categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/config/fault-severities").then((r) => setSevs(r.data)).catch(() => {});
  }, [assetId]);

  // Reset the decision to the configured minimum whenever the severity (and its floor) changes.
  useEffect(() => { setUseDecision(minDecision); }, [minDecision]);

  const submit = async () => {
    if (useDecision === "restricted_use" && !restrictionReason.trim()) {
      toast.error("Restricted Use needs a documented restriction reason.");
      return;
    }
    try {
      await api.post("/faults", {
        ...form, asset_id: assetId, use_decision: useDecision,
        restriction_reason: useDecision === "restricted_use" ? restrictionReason : undefined,
        photos: photos.map((p) => ({ id: p.id, name: p.name })),
      });
      toast.success("Fault reported");
      nav(assetId ? `/assets/${assetId}` : "/faults");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="flex flex-col gap-4 max-w-xl">
      <h1 className="font-display text-2xl font-black tracking-tight text-slate-900">Report a Fault</h1>
      {asset && <div className="bg-slate-100 rounded p-3 font-mono text-sm">{asset.asset_number} · {asset.name}</div>}
      <div>
        <label className="font-body text-sm font-semibold text-slate-700">Title (short summary)</label>
        <Input data-testid="fault-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="h-11 border-2 mt-1" placeholder="e.g. N/S tyre damaged" />
      </div>
      <div>
        <label className="font-body text-sm font-semibold text-slate-700">Priority</label>
        <div className="mt-1"><PrioritySelect testid="fault-priority" value={form.priority} onChange={(v) => set("priority", v)} className="w-full h-11" /></div>
      </div>
      <div>
        <label className="font-body text-sm font-semibold text-slate-700">Description *</label>
        <Textarea data-testid="fault-description" value={form.description} onChange={(e) => set("description", e.target.value)} className="border-2 mt-1" rows={4} />
      </div>
      <div>
        <label className="font-body text-sm font-semibold text-slate-700">Category</label>
        <select data-testid="fault-category" className="w-full h-11 border-2 border-slate-300 rounded px-2 mt-1" value={form.category_id} onChange={(e) => set("category_id", e.target.value)}>
          <option value="">Select…</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div>
        <label className="font-body text-sm font-semibold text-slate-700">Severity</label>
        <select data-testid="fault-severity" className="w-full h-11 border-2 border-slate-300 rounded px-2 mt-1" value={form.severity_id} onChange={(e) => set("severity_id", e.target.value)}>
          <option value="">Select…</option>
          {sevs.map((s) => <option key={s.id} value={s.id}>{s.name}{s.blocks_use ? " (blocks use)" : ""}</option>)}
        </select>
      </div>

      {/* Use decision — Stop Using / Restricted Use / Safe to Continue */}
      <div data-testid="fault-use-decision">
        <label className="font-body text-sm font-semibold text-slate-700">What should happen to this asset?</label>
        {minDecision !== "safe_to_continue" && (
          <p data-testid="use-decision-floor" className="text-xs text-slate-500 mt-0.5">
            This severity requires at least <b>{DEC.find((d) => d.key === minDecision)?.label}</b> — you may only choose the same or a stricter option.
          </p>
        )}
        <div className="mt-2 flex flex-col gap-2">
          {allowed.map((d) => (
            <button key={d.key} type="button" data-testid={`use-decision-${d.key}`} onClick={() => setUseDecision(d.key)}
              className={`text-left rounded-lg border-2 px-3 py-2 transition-colors ${useDecision === d.key ? d.cls + " ring-2 ring-offset-1 ring-slate-400" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
              <div className="font-body font-bold text-sm">{d.label}</div>
              <div className="text-xs opacity-80">{d.desc}</div>
            </button>
          ))}
        </div>
        {useDecision === "restricted_use" && (
          <div className="mt-2">
            <label className="font-body text-sm font-semibold text-slate-700">Restriction basis / reason *</label>
            <Textarea data-testid="fault-restriction-reason" value={restrictionReason} onChange={(e) => setRestrictionReason(e.target.value)} className="border-2 mt-1" rows={2} placeholder="e.g. Speed limited to yard use only until repaired" />
          </div>
        )}
      </div>

      <Input data-testid="fault-location" placeholder="Location (optional)" value={form.location} onChange={(e) => set("location", e.target.value)} className="h-11 border-2" />
      <div className="flex items-center gap-3 flex-wrap">
        <PhotoUpload category="fault" entityType="fault" onUploaded={(f) => setPhotos((p) => [...p, f])} label="Add Photo/Evidence" />
        <div className="flex gap-2">{photos.map((p) => <EvidenceThumb key={p.id} fileId={p.id} />)}</div>
      </div>
      <Button data-testid="fault-submit" onClick={submit} disabled={!form.description} className="h-12 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold uppercase">Submit Fault</Button>
    </div>
  );
}
